/**
 * French catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 1.
 *
 * `dir: 'ltr'`. Native-grade Modern French at a mathematician's register. The load-bearing
 * epistemic vocabulary follows the established French rendering of the Toulmin argument
 * model: **Warrant → « Garantie »** (the term French logicians use for Toulmin's warrant).
 *
 * W/M grades stay Latin (`::numbering-system/latn`) — a grade is a stable id, not prose, so
 * it never localizes; content numbers localize naturally through `Intl`. CLDR cardinal
 * categories for French are one / many / other (`many` covers the large-number form), so
 * `frameActive` declares exactly those (plus an explicit `=0` refinement). Per INV-STABLE-ID,
 * any imperfection here is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const FR_CATALOG = {
    locale: 'fr',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Enquête',
        [P2.sectionClaims]: 'Affirmations',
        [P2.sectionIntegrity]: 'Intégrité',
        [P2.axisWarrantLabel]: 'Garantie',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirique',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Définition} interp {Interprétation} testable {Testable} tested {Testé} refuted {Réfuté} program {Programme de recherche} philos {Philosophique} other {Inconnu}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Niveau de force {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Explorable',
        [P2.stateRefuted]: 'Réfuté',
        [P2.stateOutside]: 'Hors champ',
        [P2.verdictConflict]: 'En conflit avec {constraints}',
        [P2.verdictOutside]: 'Nécessite {missing}',
        [P2.lossRegisterPlain]: 'Simplifié en langage courant — une partie de la précision est omise.',
        [P2.lossRegisterKid]: 'Simplifié pour les jeunes lecteurs — de nombreux détails sont omis.',
        [P2.lossProfileDetail]: 'Le profil complet Garantie / Empirique / Modal est résumé ici.',
        [P2.lossConflictDetail]: 'Les détails du conflit sont abrégés dans cette vue.',
        [P2.laneVerified]: 'Vérifié',
        [P2.laneUnverified]: 'Non vérifié',
        [P2.laneConflict]: 'Conflit prouvé',
        [P2.constraintLogic]: 'Logique',
        [P2.constraintLightBends]: 'La lumière est déviée dans un champ gravitationnel',
        [P2.constraintParallelPostulate]: 'Le postulat des parallèles',
        [P2.claimScalarGravity]: 'Théorie scalaire de la gravité',
        [P2.claimNonEuclidean]: 'Géométrie non euclidienne',
        [P2.claimSubstrateSr]: 'Interprétation par substrat de la relativité restreinte',
        [P2.edgeExtObservation]: 'Observation externe',
        [P2.edgeResourceSample]: 'Ressource de calcul partagée',
        [P2.edgeHeuristicSample]: 'Orientation heuristique',
        [P2.edgeSharedSample]: 'Fondement partagé',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Contraintes',
        [P2.sectionEdges]: 'Liens',
        [P2.sectionFrame]: 'Cadre',
        [P2.sectionExposure]: 'Exposition',
        [P2.statusAdoptedFormal]: 'Adopté (formel)',
        [P2.statusAdoptedInterpretive]: 'Adopté (interprétatif)',
        [P2.statusSuspended]: 'Suspendu',
        [P2.edgeEffectConstraint]: 'Compose une contrainte (peut réfuter)',
        [P2.edgeEffectResource]: 'Ressource — sans effet sur les verdicts',
        [P2.edgeEffectHeuristic]: 'Heuristique — sans effet sur les verdicts',
        [P2.edgeEffectShared]: 'Fondement partagé — sans effet sur les verdicts',
        [P2.frameActive]: '{count, plural, =0 {Aucune contrainte active} one {# contrainte active} many {# contraintes actives} other {# contraintes actives}}',
        [P2.exposureNone]: 'Aucune dépendance interprétative',
        [P2.exposureDep]: 'Dépend de {id}',
        [P2.laneTrackMachine]: 'Machine',
        [P2.laneTrackExpert]: 'Expert',
        [P2.laneTrackCommunity]: 'Communauté',
        [P2.expertAudited]: 'Audité',
        [P2.expertInProgress]: 'En cours',
        [P2.expertNotReviewed]: 'Non examiné',
        [P2.communityFlags]: 'Signalements présents',
        [P2.communityNone]: 'Aucun signalement',
        [P2.controlChallenge]: 'Contester',
        [P2.controlActivate]: 'Activer',
        [P2.controlMakeInterpretive]: 'Rendre interprétatif',
        [P2.controlImportEdge]: 'Importer un lien',
        [P2.controlPromote]: 'Promouvoir',
        [P2.controlUndo]: 'Annuler',
        [P2.controlRedo]: 'Rétablir',
        [P2.controlRegister]: 'Enregistrer',
        [P2.promoteRejected]: 'Promotion refusée',
        [P2.promoteAccepted]: 'Promotion acceptée',
        [P2.promoteReasonRefutationTerminal]: 'Une preuve ne peut pas guérir une réfutation.',
        [P2.promoteReasonKeystone]: 'Une promotion clé de voûte nécessite un appui empirique.',
        [P2.promoteReasonEmpiricalNotFixed]: "Le type empirique n'est pas fixé.",
        [P2.promoteReasonModalNotFixed]: "Le degré modal n'est pas fixé.",
        [P2.promoteReasonWarrantNotImproved]: "Le degré de garantie n'est pas amélioré.",
        [P2.evidenceComputation]: "{engine} a calculé {op} = {result} sous {config}",
        [P2.lossNumericRounding]: "Résultat arrondi à {dp} décimales",
    },
};
//# sourceMappingURL=fr.js.map